'use client';

import InstantSearch from 'components/HOC/InstantSearch';
import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import useLocale from 'helpers/hooks/useLocale';
import { TasticProps } from 'frontastic/tastics/types';
import {
  FacetConfiguration,
  P2Badge,
  PriceConfiguration,
  ProductListingContent,
  ProductListingPage,
} from 'components/commercetools-ui/organisms/product/product-list-algolia/types';
import { InstantSearchServerState, useClearRefinements } from 'react-instantsearch-core';
import ProductListProvider, { useProductList } from '../product-list-algolia/context';
import LocalizedIndex from 'providers/algolia/localized-index';
import InsightsMiddleware from 'components/HOC/InstantSearch/middlewares/InsightsMiddleware';
import { history } from 'instantsearch.js/es/lib/routers';
import { UiState } from 'instantsearch.js';
import {
  attributeMapping,
  attributeMappingValue,
  stateMappingtoAttributes,
  stateMappingtoAttributesValue,
} from 'algolia/product-list';

import usePath from 'helpers/hooks/usePath';
import { getLocalizationInfo } from 'project.config';
import { BreadCrumb } from 'shared/types/content/BreadCrumb';
import ProductList from 'components/commercetools-ui/organisms/product/product-list-algolia';
import ProductListRenderComponent from './components/ProductListRender';
import { COLOR_HEX_CONFIG } from 'helpers/constants/colors';
import { PRICE_KEYS } from 'helpers/constants/algolia';
import facetsFromJson from '../../../../../../frontend/public/algoliaFacetData.json';
import { useAmplience } from 'frontastic';
import Loading from 'components/commercetools-ui/atoms/loading';

export type Color = {
  hex: string;
  name: string;
  urlName: string;
};
export type KeyValue = {
  [key: string]: string;
};

export type Banner = {
  positionNumber: number;
  bannerPosition: string;
  isEnabled: boolean;
};

export interface Props {
  slug: string;
  searchQuery?: string;
  facetsConfiguration: FacetConfiguration[];
  pricesConfiguration: PriceConfiguration[];
  serverUrl: string;
  serverState?: InstantSearchServerState;
  data: {
    dataSourceId: string;
    dataSource: Record<string, any>;
    hexCodeConfigJSON?: string;
  };
  amplienceData?: any;
  width?: string;
  height?: string;
  fontColour?: string;
  starColour?: string;
  horizontalAlignment?: string;
  bannerConfiguration?: Banner[];
  keyLabelMap: KeyValue;
}

function Hit({ hit }: { hit: any }) {
  return (
    <div className="mb-2 rounded border p-2">
      <strong>{hit.name['en-GB']}</strong>
    </div>
  );
}

function getMatchingFacetValues(
  facet: string,
  values: string[],
  stateMappingtoAttributes: Record<string, string>,
  keyLabelMap: Record<string, string>,
  availableFacatKeyValues: any,
): string[] {
  const key = keyLabelMap[facet];
  if (!key || !availableFacatKeyValues[key]) return [];

  const availableValues = availableFacatKeyValues[key];
  const normalizedMap = new Map<string, string>();
  // Build a map with normalized keys for fast lookup
  for (const original of availableValues) {
    normalizedMap?.set(normalizeValue(original), original);
  }

  // Normalize input and get matches
  return values?.map((val) => normalizedMap?.get(normalizeValue(val)))?.filter((v): v is string => !!v); // Filter out undefined
}
function normalizeValue(value: string): string {
  return value?.replace(/[_/]+/g, '-')?.replace(/\s+/g, '-')?.toLowerCase()?.trim();
}

function ProductListTastic({
  categories,
  data,
  slug,
  algoliaData,
  indexName,
  amplienceData,
  colours,
  trustBoxCss,
}: TasticProps<Props>) {
  const { getContentByDeliveryKey } = useAmplience();
  const topAndBottomContentData = amplienceData;
  const [topAndBottomContent, setTopAndBottomContent] = useState<any>(topAndBottomContentData);
  const breadCrumbs: BreadCrumb[] = data?.data?.dataSource?.breadCrumb || [];
  const p1BadgeData = data?.data?.dataSource?.p1BadgeData?.productListingContent;
  const p2BadgeData = data?.data?.dataSource?.p2BadgeData?.productListingContent as P2Badge[] | undefined;
  const isCategoryPage = data?.data?.dataSource?.isCategoryPage || false;
  const productListingContent = data?.data?.dataSource?.productListingContent as ProductListingPage | undefined;
  const { href: currentBreadCrumbUrl = '' } = breadCrumbs[breadCrumbs.length - 1] || {};
  const { updateFacetsConfiguration, updatePricesConfiguration } = useProductList();
  // const slug = useSlug();
  const { ctLocale: locale } = useLocale();
  const BrandAndSubCategory = data?.data?.dataSource?.Brands?.body;
  const { currency } = getLocalizationInfo(locale);
  const keyLabelMap: KeyValue = {};
  data?.facetsConfiguration?.forEach(({ key, label }) => {
    keyLabelMap[key] = label;
  });
  // const categorySlug = (slug as string)?.split('/').join('_');

  const categoryContent = productListingContent?.contents?.find(
    (content: ProductListingContent) => content?.slug === `/${slug}`,
  );

  const pathname = usePathname();
  let searchQuery = '';
  if (pathname.startsWith('/search/')) {
    const searchKeyMatch = pathname.match(/^\/search\/([^/]+)(\/|$)/);
    searchQuery = searchKeyMatch ? decodeURIComponent(searchKeyMatch[1]) : '';
  }

  const { refine: clearAllRefinements } = useClearRefinements();

  const { pathWithoutQuery, absolutePath } = usePath();

  const facetsConfiguration = useMemo<Record<string, FacetConfiguration>>(() => {
    return (data.facetsConfiguration ?? []).reduce(
      (acc, configuration) => ({
        ...acc,
        [configuration.key.replace(/\{currency\}/, currency)]: {
          type: configuration.type,
          label: configuration.label,
        } as FacetConfiguration,
      }),
      {},
    );
  }, [data.facetsConfiguration, currency]);

  const pricesConfiguration = useMemo<Record<string, PriceConfiguration>>(() => {
    return (data.pricesConfiguration ?? []).reduce(
      (acc, configuration) => ({
        ...acc,
        [configuration.key]: {
          ranges: configuration.ranges,
        } as PriceConfiguration,
      }),
      {},
    );
  }, [data.pricesConfiguration]);

  useLayoutEffect(() => {
    if (isCategoryPage && !window.location.href.includes(currentBreadCrumbUrl)) {
      window.history.replaceState({}, '', currentBreadCrumbUrl);
    }
  }, [isCategoryPage, currentBreadCrumbUrl]);

  // Render the page if page params greater than equal to 2
  useEffect(() => {
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      const pageParam = Number(params.get('page'));
      if (pageParam >= 2) {
        window.location.reload();
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    updateFacetsConfiguration(facetsConfiguration);
    updatePricesConfiguration(pricesConfiguration);
  }, [facetsConfiguration, pricesConfiguration, updateFacetsConfiguration, updatePricesConfiguration]);

  useEffect(() => {
    clearAllRefinements();
  }, [clearAllRefinements, pathWithoutQuery]);
  useEffect(() => {
    if (slug && typeof slug === 'string') {
      (async () => {
        const response = await getContentByDeliveryKey(slug);
        const content = response?.body ? JSON.parse(response.body) : response;
        let selectedContent = null;

        // Check if fullContent exists and does not have an error code
        if (content.fullContent && !content.fullContent.error) {
          selectedContent = content.fullContent;
        } else if (content.brandContent) {
          selectedContent = content.brandContent;
        } else {
          selectedContent = content;
        }
        setTopAndBottomContent(selectedContent);
      })();
    }
  }, [slug, getContentByDeliveryKey]);

  return (
    <ProductListRenderComponent
      slug={slug as string}
      searchQuery={searchQuery}
      categories={categories}
      breadCrumbs={breadCrumbs}
      facetsConfiguration={facetsConfiguration}
      categoryContent={categoryContent}
      algoliaData={algoliaData}
      indexName={indexName as string}
      p1BadgeData={p1BadgeData}
      p2BadgeData={p2BadgeData}
      amplienceData={topAndBottomContent}
      colours={colours}
      trustBoxCss={trustBoxCss}
      bannerConfiguration={data?.bannerConfiguration?.[0]}
      keyLabelMap={keyLabelMap}
    />
  );
}

function ProductListColourComponent({ data, ...props }: TasticProps<Props>) {
  const keyLabelMap: KeyValue = Object?.assign(
    {},
    ...(data?.facetsConfiguration?.length
      ? [Object?.fromEntries(data?.facetsConfiguration?.map(({ key, label }) => [key, label]))]
      : [{ ...stateMappingtoAttributes, ...attributeMapping }]),
  );
  const trustBoxCss = {
    width: data?.width ?? '100%',
    height: data?.height ?? '18px',
    fontColour: data?.fontColour ?? '#1E1E1E',
    starColour: data?.starColour ?? '#1E1E1E',
    horizontalAlignment: data?.horizontalAlignment ?? 'center',
  };
  const { algoliaData } = props;
  const breadCrumbs: BreadCrumb[] = data?.data?.dataSource?.breadCrumb || [];
  const { categoryName, categorySlug, facetData } = algoliaData;
  const hexCodeConfigJSON =
    typeof (data as any)?.hexCodeConfigJSON === 'string' ? (data as any)?.hexCodeConfigJSON : '[]';

  let COLORS: Color[];

  if (typeof hexCodeConfigJSON === 'string' && hexCodeConfigJSON?.trim() !== '') {
    try {
      COLORS = JSON.parse(hexCodeConfigJSON);
    } catch (error) {
      console.error('Failed to parse hexCodeConfigJSON:', error);
      COLORS = COLOR_HEX_CONFIG;
    }
  } else {
    COLORS = COLOR_HEX_CONFIG;
  }

  const { ctLocale: locale, getCurrency } = useLocale();
  let indexName: string = process.env.NEXT_PUBLIC_ALGOLIA_PRODUCTS_INDEX_US ?? '';
  if (getCurrency() !== 'GBP') {
    indexName = process.env.NEXT_PUBLIC_ALGOLIA_PRODUCTS_INDEX_DE ?? indexName;
  } else if (locale === 'en' || locale === 'en_GB') {
    indexName = process.env.NEXT_PUBLIC_ALGOLIA_PRODUCTS_INDEX_US ?? indexName;
  } else {
    indexName = process.env.NEXT_PUBLIC_ALGOLIA_PRODUCTS_INDEX_DE ?? indexName;
  }
  const facets = [facetData];
  const facetFromAutomation = facets?.find((facet) => facet?.index === indexName)?.facets;
  const facetValuesWithKeys = facetFromAutomation || facetsFromJson?.[0]?.facets;
  const currency = getCurrency();
  const priceAttributes = currency === 'GBP' ? PRICE_KEYS?.GB : PRICE_KEYS?.EU;
  const categoryUrl = decodeURIComponent(categorySlug?.split('_')?.join('/'));
  const [currentUrl, setCurrentUrl] = useState(categoryUrl);
  const hexToUrlNameMap = new Map(COLORS.map((c) => [c.hex.toLowerCase(), c.urlName]));
  const urlNameToHexMap = new Map(COLORS.map((c) => [c.urlName, c.hex.toLowerCase()]));
  const categoryPageId = facetValuesWithKeys?.['categories.en-GB.lvl1'];
  if (typeof window !== 'undefined') {
    // Early exit if we don't need to process further
    const searchParams = new URLSearchParams(window.location.search);
    const paramKeys = Array.from(searchParams?.keys());

    // Check for Salesforce params first (fastest check)
    const hasSalesforceParams = paramKeys?.some(
      (key) => key?.includes('srule') || key?.includes('pref') || key?.includes('dwvar') || key?.includes('dwcont'),
    );

    if (hasSalesforceParams) {
      // Get last breadcrumb (only if needed)
      const breadCrumb = breadCrumbs?.[breadCrumbs?.length - 1];
      const cleanUrl = breadCrumb?.href?.replace(/^\/|\/$/g, '') || '';

      window.location.replace(window.location.origin + '/' + cleanUrl);
      return <Loading />;
    }
  }
  // Build the URL from the UI state. When a sortBy value exists, it’s inserted into a dedicated URL segment.
  const routeStateToUrl = (routeState: UiState, location: Location) => {
    let newUrl = `/${categoryUrl}/`;
    const urlParts: string[] = [];
    const queryParams: string[] = [];

    // Parse existing query params from the current URL
    const existingParams = new URLSearchParams(location.search);
    existingParams.forEach((value, key) => {
      // Avoid duplicating params you already handle
      if (!['min_price', 'max_price', 'page'].includes(key)) {
        queryParams.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
      }
    });

    Object.entries(routeState).forEach(([key, value]) => {
      if (key === 'sortBy' || key === 'page' || key === 'query') return;

      let actualValue = Array.isArray(value) ? value.filter((item: string) => item !== '') : [];

      if (key === 'categories.en-GB.lvl1') {
        actualValue = actualValue?.map((item) => item?.split(' > ')?.[1]);
      }

      if (key === priceAttributes && actualValue.length) {
        const [min, max] = actualValue[0]?.split(':');
        if (min) queryParams.push(`min_price=${min}`);
        if (max) queryParams.push(`max_price=${max}`);
        return;
      }

      if (actualValue.length && key !== priceAttributes && key !== 'standalonePriceSort') {
        let formattedValue: string;
        if (key === 'colour' || key === 'color') {
          formattedValue = actualValue
            .map((hex) => hexToUrlNameMap.get(hex.toLowerCase()) || hex.toLowerCase())
            .join(',')
            .toLowerCase();
        } else {
          formattedValue = actualValue
            ?.map((val) => {
              // Apply hyphens only for brand facets
              if (
                key === 'brand' ||
                keyLabelMap[key] === 'brand' ||
                key === 'department' ||
                keyLabelMap[key] === 'department'
              ) {
                return val?.replaceAll('/', '-')?.replaceAll(' ', '-')?.replace(/_{3,}/g, '-_-')?.toLowerCase();
              }
              // Keep underscores for other facets
              return val?.replaceAll('/', '_')?.replaceAll(' ', '_')?.replace(/_{3,}/g, '_-_')?.toLowerCase();
            })
            ?.join(',');
        }

        if (formattedValue) {
          urlParts.push(`${keyLabelMap[key]}/${formattedValue}`);
        }
      }
    });

    if (routeState?.query) {
      newUrl += `search/${encodeURIComponent(routeState?.query as string)}/`;
    }

    if (urlParts.length > 0) {
      newUrl += `shop-by/${urlParts[0]}/`;
      if (urlParts.length > 1) {
        newUrl += urlParts?.slice(1)?.join('/');
      }
    }

    if (routeState?.sortBy) {
      const sortByIndex = routeState?.sortBy as string;
      const URLSortBySlug = sortByIndex?.split(`${indexName}_`)[1];
      if (!newUrl.endsWith('/')) {
        newUrl += '/';
      }
      newUrl += `sort-by/${URLSortBySlug}/`;
    }

    if (routeState?.page) {
      queryParams.push(`page=${routeState?.page}`);
    }

    if (queryParams.length > 0) {
      newUrl += (newUrl?.includes('?') ? '&' : '?') + queryParams.join('&');
    }

    newUrl = newUrl?.replace(/undefined/g, '').replace(/\/{2,}/g, '/');

    updateCanonicalLink(newUrl);
    setCurrentUrl(newUrl);
    return newUrl;
  };
  // Parse the URL to extract the UI state. This function extracts the sortBy value from the dedicated segment.
  const urlToRouteState = (url: string, searchParams: string) => {
    const params = new URLSearchParams(searchParams);
    const pageNumber = parseInt(new URLSearchParams(location?.search)?.get('page') || '0', 10);

    let sortBy;
    const sortByMatch = url?.match(/\/sort-by\/([^/]+)\//);
    if (sortByMatch && sortByMatch[1]) {
      sortBy = `${indexName}_` + sortByMatch[1];
    }

    const urlWithoutSortBy = url?.replace(/\/sort-by\/[^/]+\//, '/');
    const shopByQuery = urlWithoutSortBy.split('/shop-by/')[1]?.replace(/\/$/, '');
    const refinementList: Record<string, string[]> = {};

    const minPrice = params?.get('min_price');
    const maxPrice = params?.get('max_price');
    if (minPrice || maxPrice) {
      // convert to lowest currency unit (e.g., cents)
      const min = minPrice !== null ? (parseFloat(minPrice) * 100)?.toFixed(0) : '';
      const max = maxPrice !== null ? (parseFloat(maxPrice) * 100)?.toFixed(0) : '';
      refinementList[priceAttributes] = [`${min}:${max}`];
    }

    if (shopByQuery) {
      const facets = shopByQuery?.split('/');
      for (let i = 0; i < facets.length; i += 2) {
        const facetKey = decodeURIComponent(facets[i]);
        let facetValues = decodeURIComponent(facets[i + 1] || '')
          .split(',')
          .map((val) => {
            const normalized = val.toLowerCase();
            if (facetKey === 'colour' || facetKey === 'color') {
              const hexLower = urlNameToHexMap.get(normalized);
              return hexLower ? hexLower.toUpperCase() : val;
            }
            return normalized.replace(/_/g, ' ');
          });

        facetValues = facetValues?.map((val) => transformValue(facetKey, val));
        if (facetKey && facetValues?.length) {
          refinementList[keyLabelMap[facetKey]] = facetValues;
        }
        if (keyLabelMap[facetKey] === 'categories.en-GB.lvl1') {
          const categoriesResult: string[] = categoryPageId?.filter((item: string) => {
            if (!item?.includes('>') || item?.includes('brands')) return false;
            const formattedSplit = item?.split('>')[1]?.trim().toLowerCase();
            return facetValues?.some((value: string) => formattedSplit === value?.replaceAll('-', ' '));
          });

          refinementList[keyLabelMap[facetKey]] = categoriesResult;
        } else {
          const facetedResult = getMatchingFacetValues(
            facetKey,
            facetValues,
            stateMappingtoAttributes,
            keyLabelMap,
            facetValuesWithKeys,
          );
          if (facetedResult?.length > 0) {
            refinementList[keyLabelMap[facetKey]] = facetedResult;
          } else {
            refinementList[keyLabelMap[facetKey]] = facetValues;
          }
        }
      }
    }

    const routeState: any = { ...refinementList };
    if (sortBy) {
      routeState.sortBy = sortBy;
    }
    if (pageNumber) {
      routeState.page = pageNumber;
    }

    if (minPrice && maxPrice) {
      routeState.min_price = minPrice;
      routeState.max_price = maxPrice;
    }
    let query = '';
    if (url.startsWith('/search/')) {
      const searchKeyMatch = url.match(/\/search\/([^/]+)\//);
      query = searchKeyMatch ? decodeURIComponent(searchKeyMatch[1]) : '';
    }

    if (query) {
      routeState.query = query;
    }

    return routeState;
  };

  const transformValue = (facet: string, val: string): string => {
    const numericPrefixPattern = /^(\d+(\.\d+)?_\d+(\.\d+)?)(.*)/; // e.g., 9_10_years
    const numericRangePattern = /^\d+(\.\d+)?_\d+(\.\d+)?$/; // e.g., 6_11
    const unitPattern = /^(\d+)_([a-zA-Z]+)$/; // e.g., 90_cm

    const slashToForwardFacets = ['size', 'brand'];
    const dashToSpaceFacets = ['offer'];
    const collectionFacets = ['collection'];

    const match = val?.match(numericPrefixPattern);
    if (match) {
      const numericPart = match?.[1]; // "9_10"
      const remainingPart = match?.[4]; // "_years"
      const transformedRemaining = remainingPart?.replace(/_/g, ' ');
      return numericPart?.replace(/_/g, '-') + transformedRemaining; // "9-10 years"
    }

    if (numericRangePattern?.test(val)) {
      return val?.replace(/_/g, '-'); // keep "6-11", "8-12" as-is
    }

    if (slashToForwardFacets?.includes(facet) && val?.includes('_')) {
      const unitMatch = val?.match(unitPattern); // e.g., 90_cm
      if (unitMatch) {
        return `${unitMatch?.[1]} ${unitMatch?.[2]}`; // "90 cm"
      }
      if (val?.toLowerCase().includes('size')) {
        return val.replace(/_/g, ' '); // e,g one_size -> one size
      }
      // if underscore is between letters and numbers, replace with space
      return val?.replace(/_/g, (match, offset, string) => {
        const before = string[offset - 1];
        const after = string[offset + 1];
        const isLetter = (c: string) => /[a-zA-Z]/?.test(c);
        const isNumber = (c: string) => /[0-9]/?.test(c);

        if (before && after) {
          if (isLetter(before) && isLetter(after)) {
            return '/'; // letters_letters => slash  // "large_xl" => "large/xl", g_fore => g/fore
          }
          if ((isLetter(before) && isNumber(after)) || (isNumber(before) && isLetter(after))) {
            return ' '; // letter_number or number_letter => space // axis_10" => "axis 10"
          }
        }
        return '/'; // default to slash
      });
    }

    if (dashToSpaceFacets?.includes(facet)) {
      return val
        ?.replace(/_/g, ' ')
        ?.trim()
        ?.replace(/\b\w/g, (c) => c?.toUpperCase()); // eg. 3_for_2___mix_and_match => 3 For 2 - Mix And Match , 3_offer_test => 3 offer test
    }

    if (collectionFacets?.includes(facet)) {
      return val
        ?.replace(/_(\d+)$/, ' $1')
        ?.replace(/_/g, '-')
        ?.replace(/\b\w/g, (c) => c?.toUpperCase()); // capitalize each word // e.g, hi_toe_3 => Hi-Toe 3
    }

    return val?.replace(/\//g, '-')?.replace(/_/g, ' '); // default: slashes to dashes, underscores to spaces
  };

  // Define the router and state mapping for InstantSearch.
  const routing = {
    router: history({
      createURL({ qsModule, routeState, location }) {
        return `${location.origin}${routeStateToUrl(routeState, location)}`;
      },
      parseURL({ qsModule, location }): UiState {
        return urlToRouteState(location.pathname, location.search) as unknown as UiState;
      },
    }),
    stateMapping: {
      stateToRoute(uiState: { [key: string]: any }) {
        const indexUiState = uiState[indexName] || {};
        const configureUiState = uiState[''] || {};
        const { refinementList, range, sortBy, page, query } = indexUiState;

        const stateToRoutes: { [key: string]: any } = {};
        if (refinementList) {
          Object.entries(refinementList).forEach(([facet, value]: any) => {
            if (facet && value && value.length > 0 && Array.isArray(value)) {
              stateToRoutes[facet] = value.map((item: string) => item.toUpperCase().replaceAll('-', ' ')).sort();
            }
          });
        }
        if (range) {
          Object.entries(range)?.forEach(([facet, value]: any) => {
            if (facet === priceAttributes && value) {
              const [min, max] = value?.split(':');
              const formattedMin = min ? (parseFloat(min) / 100)?.toFixed(2) : '';
              const formattedMax = max ? (parseFloat(max) / 100)?.toFixed(2) : '';
              stateToRoutes[facet] = [`${formattedMin}:${formattedMax}`];
            }
          });
        }
        if (sortBy) {
          stateToRoutes.sortBy = sortBy;
        }
        if (page) {
          stateToRoutes.page = page;
        }
        if (query || configureUiState?.configure?.query) {
          stateToRoutes.query = query || configureUiState?.configure?.query;
        }

        return stateToRoutes;
      },
      routeToState(routeState: any) {
        // Handle price range conversion
        if (routeState[priceAttributes]) {
          const [min, max] = routeState[priceAttributes]?.[0]?.split(':');
          routeState[priceAttributes] = [`${min || ''}:${max || ''}`];
        }

        return {
          [indexName]: {
            refinementList: { ...routeState },
            sortBy: routeState.sortBy || undefined,
            page: parseInt(routeState?.page) || 0,
            query: routeState.query || '',
            range: {
              [priceAttributes]: routeState[priceAttributes]?.[0] || undefined,
            },
          },
        };
      },
    },
  };

  // Helper function to update canonical tag
  const updateCanonicalLink = (url: string) => {
    if (typeof window === 'undefined') return; // Skip during SSR
    const absoluteUrl = url.startsWith('http') ? url : new URL(url, window.location.origin).toString();

    let canonicalLink = document.querySelector("link[rel='canonical']");
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', absoluteUrl);
  };
  const normalizeUrl = (url: string) => url?.replace(/^\/|\/$/g, '');
  return (
    <>
      <InstantSearch categorySlug={categoryName} routing={routing} insights>
        <LocalizedIndex type="products">
          <ProductListProvider>
            <ProductListTastic
              data={{ ...data }}
              indexName={indexName}
              slug={normalizeUrl(currentUrl) as string}
              {...props}
              colours={COLORS}
              trustBoxCss={trustBoxCss}
            />
          </ProductListProvider>
        </LocalizedIndex>
      </InstantSearch>
    </>
  );
}
export default ProductListColourComponent;
